import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import roleConfig from "../../common/layout/roleConfig";
import ChoixChips from "../../common/ChoixChips/ChoixChips";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import { EmptyState, ListPageHeader } from "../../common/DataList";
import ListeRegles from "./ListeRegles";
import SchemaRegles from "./SchemaRegles";
import ModaleRegle from "./ModaleRegle";
import ModaleVersion from "./ModaleVersion";
import PanneauHistorique from "./PanneauHistorique";
import { jourFr, libelleVersion, pluriel, versionParDefaut } from "./reglesSousOrientation";
import { listerVersions, supprimerVersion, voirReferentiel, voirVersion } from "./reglesSousOrientationApi";
import "./regles-sous-orientation.scss";

// ============================================================
// Règles de sous-orientation (CE-1419, maquette 11) : les versions datées
// des règles qui confient un allocataire orienté vers la Caf à un opérateur.
// Le super administrateur ajoute, modifie, place et supprime les règles et
// prépare une version ; l'administrateur Caf consulte (privilège
// `edit_regles_orientation` masqué). La version en vigueur s'ouvre par
// défaut ; la liste et le schéma lisent la même version.
// ============================================================

const VUES = [
  { id: "liste", name: "Liste", icon: "list" },
  { id: "schema", name: "Schéma", icon: "project-diagram" },
];

const ReglesSousOrientation = () => {
  const [state] = useStore();
  const utilisateur = (state.auth && state.auth.user) || {};
  const peutModifier = !(roleConfig[utilisateur.role] || []).includes("edit_regles_orientation");

  const [versions, setVersions] = useState(null);
  const [versionId, setVersionId] = useState(null);
  const [version, setVersion] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [erreurVersion, setErreurVersion] = useState(null);
  const [essaiVersion, setEssaiVersion] = useState(0);
  const [vue, setVue] = useState("liste");
  const [referentiel, setReferentiel] = useState(null);
  const [regleOuverte, setRegleOuverte] = useState(undefined);
  const [modaleVersion, setModaleVersion] = useState(false);
  const [suppressionVersion, setSuppressionVersion] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [historique, setHistorique] = useState(false);
  const refTitre = useRef(null);
  // Une réponse arrivée après le départ de la page n'écrit plus rien.
  const monte = useRef(true);
  const versionsLues = useRef(false);
  useEffect(() => () => {
    monte.current = false;
  }, []);

  const chargerVersions = (idVoulu = null) =>
    listerVersions().then(
      (lues) => {
        if (!monte.current) return;
        versionsLues.current = true;
        setVersions(lues);
        setErreur(null);
        const connus = lues.versions.map((v) => v.id);
        setVersionId((actuel) => (idVoulu && connus.includes(idVoulu) ? idVoulu : actuel && connus.includes(actuel) ? actuel : versionParDefaut(lues.versions)));
      },
      (e) => {
        if (!monte.current) return;
        // Déjà affichées, les versions restent à l'écran : seul le rechargement a échoué.
        if (versionsLues.current) toast.error(e.message || "La liste des versions n'a pas pu être rechargée.");
        else setErreur(e.message || "Les versions des règles n'ont pas pu être chargées.");
      }
    );

  useEffect(() => {
    chargerVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setErreurVersion(null);
    if (!versionId) {
      setVersion(null);
      return;
    }
    let actif = true;
    setVersion(null);
    voirVersion(versionId).then(
      (lue) => actif && setVersion(lue),
      (e) => actif && setErreurVersion(e.message || "Erreur inconnue.")
    );
    return () => {
      actif = false;
    };
  }, [versionId, essaiVersion]);

  // Le référentiel ne sert qu'à la saisie : relu à chaque ouverture, pour proposer les opérateurs tels qu'ils sont.
  const ouvrirRegle = (regle) => {
    voirReferentiel().then(
      (lu) => {
        if (!monte.current) return;
        setReferentiel(lu);
        setRegleOuverte(regle);
      },
      (e) => toast.error(e.message || "La saisie des règles n'a pas pu s'ouvrir.")
    );
  };

  const regleEnregistree = (relue, message, { supprimee = false } = {}) => {
    setRegleOuverte(undefined);
    setVersion(relue);
    toast.success(message);
    chargerVersions(relue.id);
    // La fenêtre rend le focus au bouton de la règle supprimée, qui n'existe plus : il va au titre de la page.
    if (supprimee) window.setTimeout(() => refTitre.current && refTitre.current.focus(), 0);
  };

  const versionPreparee = (preparee) => {
    setModaleVersion(false);
    toast.success(`Version ${preparee.statut.id === "A_VENIR" ? `au ${jourFr(preparee.enVigueurLe)} préparée` : "créée, en vigueur dès aujourd'hui"}.`);
    chargerVersions(preparee.id);
  };

  const confirmerSuppressionVersion = () => {
    setSuppressionEnCours(true);
    supprimerVersion(version.id).then(
      () => {
        setSuppressionEnCours(false);
        setSuppressionVersion(false);
        toast.success("Version à venir supprimée.");
        setVersionId(null);
        chargerVersions().then(() => refTitre.current && refTitre.current.focus());
      },
      (e) => {
        setSuppressionEnCours(false);
        setSuppressionVersion(false);
        toast.error(e.message || "La version n'a pas pu être supprimée.");
      }
    );
  };

  const aucuneVersion = versions && versions.versions.length === 0;
  const peutAjouter = peutModifier && version && version.modifiable;

  return (
    <div className="container-fluid px-5 my-5 list-page regles-sous-orientation">
      <ListPageHeader
        title={
          <span ref={refTitre} tabIndex={-1}>
            Règles de sous-orientation
          </span>
        }
        subtitle="Un allocataire orienté vers la Caf est confié, en accompagnement total, à l'opérateur de la première règle qui le concerne. Sinon, il reste à la Caf."
      >
        {peutAjouter && (
          <button type="button" className="btn btn-primary" onClick={() => ouvrirRegle(null)}>
            <i className="fa fa-plus mr-2" aria-hidden="true" />
            Ajouter une règle
          </button>
        )}
      </ListPageHeader>

      {!peutModifier && (
        <p className="regles-encadre" role="status">
          <i className="fa fa-lock" aria-hidden="true" />
          Vous consultez les règles ; seul un super administrateur les modifie.
        </p>
      )}

      <section className="regles-carte" aria-label="Règles de sous-orientation">
        {erreur && !versions && (
          <EmptyState icon="fa-exclamation-circle" title="Les règles n'ont pas pu être chargées" hint={erreur} erreur action={{ label: "Réessayer", onClick: () => chargerVersions() }} />
        )}
        {!erreur && !versions && <p className="regles-chargement" aria-busy="true">Chargement des règles…</p>}

        {aucuneVersion && (
          <EmptyState
            icon="fa-list-ol"
            title="Aucune version des règles"
            hint="Sans règle, tous les allocataires orientés vers la Caf y restent."
            action={peutModifier ? { label: "Créer la première version", onClick: () => setModaleVersion(true) } : null}
          />
        )}

        {versions && !aucuneVersion && (
          <>
            <div className="regles-barre">
              <div className="regles-barre__version">
                <label htmlFor="choix-version-regles">Version</label>
                <select id="choix-version-regles" className="form-control" value={versionId || ""} onChange={(e) => setVersionId(e.target.value)}>
                  {versions.versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {libelleVersion(v)}
                    </option>
                  ))}
                </select>
              </div>
              {version && <span className="regles-barre__compte">{pluriel(version.regles.length, "règle")}</span>}
              <ChoixChips name="vue-regles" legend="Affichage" legendSrOnly variant="segmente" options={VUES} value={vue} onChange={setVue} />
              <button type="button" className="btn btn-ghost" onClick={() => setHistorique(true)} disabled={!version}>
                <i className="fa fa-history mr-2" aria-hidden="true" />
                Historique
              </button>
              {peutModifier && versions.peutPreparer && (
                <button type="button" className="btn btn-ghost" onClick={() => setModaleVersion(true)}>
                  <i className="fa fa-copy mr-2" aria-hidden="true" />
                  Préparer une version
                </button>
              )}
              {peutModifier && version && version.supprimable && (
                <button type="button" className="btn btn-ghost regles-barre__danger" onClick={() => setSuppressionVersion(true)}>
                  <i className="fa fa-trash-alt mr-2" aria-hidden="true" />
                  Supprimer cette version
                </button>
              )}
            </div>

            {version && version.statut.id === "A_VENIR" && (
              <p className="regles-bandeau">
                <i className="fa fa-clock" aria-hidden="true" />
                Cette version s'appliquera le {jourFr(version.enVigueurLe)} ; d'ici là, la version en vigueur reste appliquée. Ses quotas sont ceux de {version.anneeQuotas}.
              </p>
            )}
            {version && version.statut.id === "REMPLACEE" && (
              <p className="regles-bandeau">
                <i className="fa fa-archive" aria-hidden="true" />
                Cette version a été remplacée : elle se consulte sans se modifier.
              </p>
            )}

            {erreurVersion && (
              <EmptyState
                icon="fa-exclamation-circle"
                title="La version n'a pas pu être chargée"
                hint={erreurVersion}
                erreur
                action={{ label: "Réessayer", onClick: () => setEssaiVersion((n) => n + 1) }}
              />
            )}
            {!version && !erreurVersion && <p className="regles-chargement" aria-busy="true">Chargement de la version…</p>}
            {version && version.regles.length === 0 && (
              <EmptyState
                icon="fa-list-ol"
                title="Aucune règle dans cette version"
                hint="Sans règle, tous les allocataires orientés vers la Caf y restent."
                action={peutAjouter ? { label: "Ajouter une règle", onClick: () => ouvrirRegle(null) } : null}
              />
            )}
            {version && version.regles.length > 0 && vue === "liste" && (
              <ListeRegles regles={version.regles} annee={version.anneeQuotas} peutModifier={peutAjouter} onModifier={ouvrirRegle} />
            )}
            {version && version.regles.length > 0 && vue === "schema" && (
              <SchemaRegles regles={version.regles} annee={version.anneeQuotas} peutModifier={peutAjouter} onModifier={ouvrirRegle} />
            )}
          </>
        )}
      </section>

      {version && referentiel && regleOuverte !== undefined && (
        <ModaleRegle
          version={version}
          regle={regleOuverte}
          referentiel={referentiel}
          onFermer={() => setRegleOuverte(undefined)}
          onEnregistree={regleEnregistree}
        />
      )}

      {modaleVersion && (
        <ModaleVersion
          premiere={aucuneVersion}
          nombreRegles={((versions && versions.versions.find((v) => v.statut.id === "EN_VIGUEUR")) || {}).nombreRegles || 0}
          onFermer={() => setModaleVersion(false)}
          onPreparee={versionPreparee}
        />
      )}

      <ModaleLocale
        ouvert={suppressionVersion}
        onFermer={() => setSuppressionVersion(false)}
        titre="Supprimer cette version ?"
        taille="etroite"
        idTitre="modale-suppression-version"
        peutFermer={!suppressionEnCours}
        pied={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setSuppressionVersion(false)} disabled={suppressionEnCours}>
              Garder la version
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmerSuppressionVersion} disabled={suppressionEnCours}>
              Supprimer
            </button>
          </>
        }
      >
        {version && (
          <p className="mb-0">
            La version à venir au {jourFr(version.enVigueurLe)} et ses {pluriel(version.regles.length, "règle")} seront supprimées. La version en vigueur
            n'est pas touchée.
          </p>
        )}
      </ModaleLocale>

      {historique && version && <PanneauHistorique version={version} onFermer={() => setHistorique(false)} />}
    </div>
  );
};

export default ReglesSousOrientation;
